/**
 * Start gates for a pending playUri (HEOS Amp "Kitchen", Oct 21).
 *
 * 1. A renderer that reports TRANSITIONING has accepted the URI and is still loading it —
 *    that is starting, not failed. It stays pending up to the connector's own 30 s
 *    TRANSITIONING ceiling instead of expiring at the latency-scaled window (~14 s) and
 *    inviting a re-send that restarts the device's load.
 * 2. Work that must not run until the current track is confirmed PLAYING — the gapless
 *    SetNext prefetch fired at the 8 s invoke-budget mark while the track was still
 *    loading, and the renderer went off to fetch the next file. This is a timing gate
 *    only: SetNext capability gating, position tracking and the handoff are unchanged.
 */
import { diagnosticLog } from '../utils/diagnosticLogger';
import type { PlayUriAcceptanceEvidence } from './playUriConfirmation';
import { getPlayUriPendingSession } from './playUriConfirmation';
import { START_CONFIRM_WINDOW_CAP_MS } from './playUriStartConfirmWindow';

export const TRANSITIONING_START_CEILING_MS = START_CONFIRM_WINDOW_CAP_MS;

export function stillTransitioning(ev: PlayUriAcceptanceEvidence | null, elapsedMs: number): boolean {
  return ev === 'renderer_transitioning' && elapsedMs < TRANSITIONING_START_CEILING_MS;
}

const CONFIRMED_PLAYING_REASONS = new Set(['play_uri_ok', 'confirmed_playing', 'poll_playing']);

let deferred: { trackId: string; run: () => void } | null = null;

export function resetDeferredUntilPlaying(): void {
  deferred = null;
}

/**
 * False when no play is pending for `trackId` (the caller proceeds now). Otherwise holds
 * `run` until that play is confirmed PLAYING and returns true. Only the latest is kept.
 */
export function deferUntilPlayUriConfirmed(
  trackId: string | number | null | undefined,
  run: () => void,
): boolean {
  const session = getPlayUriPendingSession();
  if (trackId == null || !session || session.trackId !== String(trackId)) return false;
  deferred = { trackId: String(trackId), run };
  diagnosticLog('info', 'playback', 'setnext_prefetch_deferred_until_playing', {
    trackId: session.trackId,
    rendererId: session.rendererId,
    elapsed_ms: Date.now() - session.dispatchedAt,
  });
  return true;
}

/** Called when a pending play clears: runs the held work on PLAYING, drops it otherwise. */
export function settleDeferredUntilPlaying(trackId: string, reason: string): void {
  const job = deferred;
  if (!job || job.trackId !== trackId) return;
  deferred = null;
  if (!CONFIRMED_PLAYING_REASONS.has(reason)) {
    diagnosticLog('info', 'playback', 'setnext_prefetch_dropped', { trackId, reason });
    return;
  }
  queueMicrotask(() => {
    try { job.run(); } catch { /* non-critical prefetch */ }
  });
}

/** A new track's play supersedes any work held for a different track. */
export function dropDeferredForOtherTrack(trackId: string): void {
  if (deferred && deferred.trackId !== trackId) settleDeferredUntilPlaying(deferred.trackId, 'superseded');
}
